/**
 * SpaceDetailBookingPage - Página completa de detalle y reserva de espacio
 * Tercera pantalla del flujo V2: muestra el espacio completo con plano, carousel y selector de fechas
 */
import React, { useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTheme } from '../hooks/useTheme'
import { useShoppingCart } from '../context/ShoppingCartContext'
import { apiClient } from '../services/api'
import { formatCurrency } from '../utils/currencyHelpers'
import { formatDateDisplay } from '../utils/dateHelpers'
import SimpleHeader from '../components/common/SimpleHeader'
import SpaceLocationMap from '../components/spaces/SpaceLocationMap'
import ImageCarousel from '../components/common/ImageCarousel'
import DateSelectorV2 from '../components/booking/DateSelectorV2'
import SafeComodidades from '../components/common/SafeComodidades'
import SpacePricingTable from '../components/booking/SpacePricingTable'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorMessage from '../components/common/ErrorMessage'
import { toastSuccess } from '../utils/toastHelper'
import type { DiaDesglose } from '../types/availability'
import type { CartItem } from '../context/ShoppingCartContext'

interface DateSelection {
  fechaEntrada: string | null
  fechaSalida: string | null
  desglose: Array<{ fecha: string; dia_semana?: string; tarifa: number }>
  montoTotal: number
  cantidadUnidades: number
}

const SpaceDetailBookingPage: React.FC = () => {
  const { tipoSlug, espacioId } = useParams<{ tipoSlug: string; espacioId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { colors } = useTheme()
  const { addItem } = useShoppingCart()

  // Obtener fechas del state de navegación (si vienen de SpacesByTypePageV2)
  const fechasIniciales = location.state as { fechaEntrada?: string; fechaSalida?: string } | null

  const [selection, setSelection] = useState<DateSelection | null>(null)

  // Fetch space data
  const { data: espacio, isLoading, error } = useQuery({
    queryKey: ['espacio', espacioId],
    queryFn: async () => {
      const response = await apiClient.get(`/api/espacios/${espacioId}`)
      return response.data
    },
    enabled: !!espacioId
  })

  const handleAgregarAlCarrito = () => {
    if (!selection || !espacio) return

    const cartItem: CartItem = {
      espacio_id: espacio.id,
      nombre: espacio.nombre,
      codigo: espacio.codigo,
      tipo_nombre: espacio.tipo_nombre || tipoSlug || '',
      fecha_entrada: selection.fechaEntrada!,
      fecha_salida: selection.fechaSalida!,
      unidad_reserva: espacio.unidad_reserva || 'noche',
      cantidad_unidades: selection.cantidadUnidades,
      tarifa_aplicada_por_unidad: selection.montoTotal / selection.cantidadUnidades,
      monto_total: selection.montoTotal,
      desglose: selection.desglose,
      imagenes: espacio.imagenes || [],
      capacidad_maxima: espacio.capacidad_maxima,
      comodidades: espacio.comodidades || []
    }

    addItem(cartItem)
    toastSuccess(`${espacio.nombre} agregado al carrito`)
    navigate('/carrito')
  }

  const isButtonEnabled = Boolean(
    selection?.fechaEntrada &&
    selection?.fechaSalida &&
    selection?.montoTotal > 0
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !espacio) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <ErrorMessage message="Error al cargar el espacio" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <SimpleHeader
        title={espacio.nombre}
        subtitle={`👥 ${espacio.capacidad_maxima} personas • 📍 ${espacio.codigo}`}
      />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 sm:py-3">
          <nav className="text-xs sm:text-sm text-gray-600 flex flex-wrap items-center gap-1">
            <button onClick={() => navigate('/reservar')} className="hover:text-gray-900">
              Tipos
            </button>
            <span className="hidden sm:inline mx-2">→</span>
            <span className="sm:hidden mx-1">›</span>
            <button onClick={() => navigate(`/reservar/${tipoSlug}`)} className="hover:text-gray-900 truncate max-w-[100px] sm:max-w-none">
              {tipoSlug}
            </button>
            <span className="hidden sm:inline mx-2">→</span>
            <span className="sm:hidden mx-1">›</span>
            <span className="text-gray-900 font-medium truncate max-w-[120px] sm:max-w-none">{espacio.nombre}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* 1. Carousel - Galería de Fotos */}
        <div className="bg-white rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📸 Galería de Fotos</h2>
          <ImageCarousel
            images={espacio.imagenes || []}
            title={espacio.nombre}
            showThumbnails={true}
            primaryColor={colors.primary}
            imageContainerClassName="h-48 sm:h-64 md:h-80 lg:h-96"
          />
        </div>

        {/* 2. Descripción */}
        <div className="bg-white rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📝 Descripción</h2>
          <p className="text-gray-700 whitespace-pre-wrap">{espacio.descripcion || 'Sin descripción disponible'}</p>
        </div>

        {/* 3. Comodidades */}
        <div className="bg-white rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">🏷️ Comodidades</h2>
          <SafeComodidades comodidades={espacio.comodidades || []} maxItems={999} />
        </div>

        {/* 4. Plano con Mano Indicadora */}
        <div className="bg-white rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📍 Ubicación en el Plano</h2>
          <SpaceLocationMap
            espacio={espacio}
            showAnimations={true}
            showContext={true}
          />
        </div>

        {/* 5. Tarifas periodo */}
        <div className="bg-white rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">💰 Tarifas</h2>
          <SpacePricingTable
            espacioId={espacio.id}
            unidadReserva={espacio.unidad_reserva || 'noche'}
          />
        </div>

        {/* 6. Calendario - Selector de Fechas */}
        <div className="bg-white rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📅 Selecciona tus Fechas</h2>
          {fechasIniciales?.fechaEntrada && fechasIniciales?.fechaSalida && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 Rango fechas: {formatDateDisplay(fechasIniciales.fechaEntrada)} - {formatDateDisplay(fechasIniciales.fechaSalida)}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Selecciona tus fechas en el calendario para ver disponibilidad y precio
              </p>
            </div>
          )}
          <DateSelectorV2
            espacioId={espacio.id}
            fechaEntradaInicial={fechasIniciales?.fechaEntrada}
            fechaSalidaInicial={fechasIniciales?.fechaSalida}
            onDateChange={setSelection}
            showPricing={true}
          />
        </div>
      </div>

      {/* Sticky Footer - Total + Botón */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-3 sm:p-4 z-50">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-4">
          <div className="text-center sm:text-left">
            <p className="text-xs sm:text-sm text-gray-600">Total a pagar</p>
            <p className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ color: colors.primary }}>
              {selection ? formatCurrency(selection.montoTotal) : '-'}
            </p>
            <p className="text-xs sm:text-sm text-gray-500">
              {selection
                ? `${selection.cantidadUnidades} ${espacio.unidad_reserva || 'noche'}${selection.cantidadUnidades !== 1 ? 's' : ''}`
                : 'Selecciona fechas para ver el precio'
              }
            </p>
          </div>

          <button
            onClick={handleAgregarAlCarrito}
            disabled={!isButtonEnabled}
            className={`
              w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-white font-semibold text-sm sm:text-base md:text-lg
              transition-all duration-200
              ${isButtonEnabled
                ? 'hover:shadow-lg hover:scale-105'
                : 'opacity-50 cursor-not-allowed'
              }
            `}
            style={{
              backgroundColor: isButtonEnabled ? colors.primary : '#9ca3af'
            }}
            onMouseEnter={(e) => {
              if (isButtonEnabled) {
                e.currentTarget.style.backgroundColor = colors.primaryDark
              }
            }}
            onMouseLeave={(e) => {
              if (isButtonEnabled) {
                e.currentTarget.style.backgroundColor = colors.primary
              }
            }}
          >
            {isButtonEnabled ? '🛒 Agregar al Carrito' : 'Selecciona fechas'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SpaceDetailBookingPage
